'use client';

import { Link } from '@/components/ui';
import { useTranslations } from 'next-intl';
import { RatingBadge } from '@/components/ui';
import PlayerAvatar from './PlayerAvatar';
import { Tables } from '@/types/supabase';
import { useFormatName } from '@/contexts/NameFormattingContext';

interface PlayerCardProps {
  player: {
    id: string;
    username: string | null;
    first_name: string;
    last_name: string;
    current_rating?: number | null;
    profile_photo?: string | null;
  };
  showRating?: boolean;
  showUsername?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'compact';
  className?: string;
  clickable?: boolean;
  flip?: boolean;
  loading?: boolean;
  rankingPoints?: number;
  pointsType?: 'rating' | 'ranking_points';
}

// Use smaller font only for long names (e.g. Malaysian/Indian names)
// Short names (Chinese, Western) keep normal size
const NAME_LENGTH_THRESHOLD = 15;

export default function PlayerCard({
  player,
  showRating = true,
  showUsername = true,
  size = 'sm',
  className = '',
  clickable = true,
  flip = false,
  loading = false,
  rankingPoints,
  pointsType: _pointsType = 'rating',
}: PlayerCardProps) {
  const t = useTranslations('Leaderboard');
  const formatName = useFormatName();
  const displayName = formatName(player.first_name, player.last_name, player.username);
  const isLongName = displayName.length > NAME_LENGTH_THRESHOLD;

  const PlayerContent = (
    <div
      className={`flex items-center ${size === 'compact' ? 'gap-2' : 'gap-3'} ${className} ${flip ? 'flex-row-reverse' : ''}`}
    >
      <div className="flex-shrink-0">
        {loading ? (
          <div
            className={`${size === 'compact' ? 'h-9 w-9' : size === 'sm' ? 'h-8 w-8' : size === 'md' ? 'h-12 w-12' : 'h-16 w-16'} bg-border animate-pulse rounded-full`}
          />
        ) : (
          <PlayerAvatar
            player={player as Tables<'player'>}
            size={size === 'compact' ? 'compact' : size}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        {loading ? (
          <>
            <div
              className={`${size === 'compact' ? 'h-4' : 'h-5'} bg-border mb-1 w-24 animate-pulse rounded`}
            />
            {showRating && <div className="bg-border h-3 w-16 animate-pulse rounded" />}
          </>
        ) : size === 'compact' ? (
          <>
            <div className={`leading-tight font-medium ${isLongName ? 'text-xs' : 'text-sm'}`}>
              {displayName}
            </div>
            {showRating && (
              <>
                {rankingPoints !== undefined ? (
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {t('rankingPoints')}: {rankingPoints}
                  </div>
                ) : player.current_rating ? (
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {t('rating')}: {player.current_rating}
                  </div>
                ) : null}
              </>
            )}
          </>
        ) : (
          <>
            <div className={`leading-tight font-medium ${isLongName ? 'text-xs' : 'text-sm'}`}>
              {displayName}
            </div>
            {showUsername && (
              <div className="text-foreground truncate text-sm">@{player.username}</div>
            )}
          </>
        )}
      </div>
      {showRating && size !== 'compact' && !loading && (
        <>
          {rankingPoints !== undefined ? (
            <RatingBadge rating={rankingPoints} />
          ) : player.current_rating ? (
            <RatingBadge rating={player.current_rating} />
          ) : null}
        </>
      )}
    </div>
  );

  if (clickable) {
    return (
      <Link
        href={`/user/${player.username}`}
        prefetch={false}
        className="block rounded-lg transition-colors hover:underline"
      >
        {PlayerContent}
      </Link>
    );
  }

  return <div className="rounded-lg">{PlayerContent}</div>;
}
